import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import OverviewCard from "./OverviewCard";
import { FormattedMessage } from "react-intl";
import placeHolder from "../../assets/placeholder.jpg";
import { shortenString } from "../../utils/shortenString";
import CardButtons from "./CardButtons";
import { useSelector } from "react-redux";
import SwiperCarousal from "../Carousal/SwiperCarousal";
import useInViewport from "../../hooks/useInViewport";

// fixed media height so layout never collapses before images load
const MEDIA_HEIGHT = { xs: 220, md: 200 };

const CarCard = (props) => {
  const isMob = useSelector((state) => state.global.isMobile);
  const [cardRef, isInView] = useInViewport({ rootMargin: "300px" });
  const [imgLoaded, setImgLoaded] = useState(false);

  const {
    title,
    defaultImg,
    bodyTypeEn,
    warrantyTillDate,
    yearOfManufacture,
    mileage,
    carSlug,
    usedCarUUID,
    images,
  } = props.data;

  const cardOverView = {
    bodyTypeEn: bodyTypeEn,
    warrantyTillDate: warrantyTillDate,
    yearOfManufacture: yearOfManufacture,
    mileage: mileage,
  };

  const carImages = useMemo(() => {
    if (!images) return [];
    return images
      .split(",")
      .map(
        (image) =>
          `https://fazaacar.s3.ap-south-1.amazonaws.com/usedcars/${image.trim()}`,
      );
  }, [images]);

  return (
    <Card
      ref={cardRef}
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderRadius: "1rem",
        height: "100%",
        "&:hover": { cursor: "pointer" },
        position: "relative",
      }}
    >
      <Link
        to={`/${carSlug}?carId=${usedCarUUID}`}
        underline="none"
        state={{ carId: usedCarUUID }}
        style={{
          textDecoration: "none",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Reserved-height wrapper: this box always has a size,
            regardless of whether the image/carousel inside has loaded */}
        <Box sx={{ position: "relative", height: MEDIA_HEIGHT, flexShrink: 0 }}>
          {/* Skeleton fills the space until something real is ready */}
          {!imgLoaded && (
            <Skeleton
              variant="rectangular"
              animation="wave"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
            />
          )}

          {isInView && carImages.length > 0 ? (
            <CardMedia
              sx={{ height: "100%" }}
              children={
                <SwiperCarousal
                  data={carImages}
                  swiperOnCard={true}
                  onImagesLoaded={() => setImgLoaded(true)}
                />
              }
            />
          ) : (
            // <CardMedia
            //   component="img"
            //   sx={{ height: "100%", objectFit: "cover" }}
            //   image={placeHolder}
            //   alt={title}
            //   loading="lazy"
            //   decoding="async"
            //   onLoad={() => setImgLoaded(true)}
            // />
            <Skeleton
              sx={{ bgcolor: "white.light" }}
              variant="rectangular"
              width="100%"
              height={200}
              animation="wave"
            />
          )}
        </Box>

        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: { xs: "-12%", md: "-9%" },
              right: "1rem",
              zIndex: 9,
            }}
          >
            <CardButtons
              status=""
              favourite={false}
              share={true}
              carname={title}
              slug={carSlug}
              carId={usedCarUUID}
            />
          </Box>
          <Typography
            variant={isMob ? "h5" : "h3"}
            sx={{ color: "secondary.main", fontSize: "26px" }}
          >
            {title}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={1}>
                {Object.entries(cardOverView).map(([key, value], index) => (
                  <Grid
                    key={index}
                    item
                    xs={3}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <OverviewCard
                      objKey={key}
                      objValue={value}
                      cardPadding=".5rem"
                      cardVariant="elevation"
                      elevation={0}
                      label={<FormattedMessage id={`app.overview.${key}`} />}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Stack>
        </CardContent>
      </Link>
    </Card>
  );
};

export default React.memo(CarCard);
